import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import AddDomainModalForm from '../CompanyDomains/components/AddDomainModalForm/AddDomainModalForm';
import getSsoIntegrationFormFields from './utils/getSsoIntegrationFormFields';
import getDefaultValuesFromQueryData from './utils/getDefaultValuesFromQueryData';
import useSsoFormActions from './utils/useSsoFormActions';
import usePresetDefaults from './utils/usePresetDefaults';
import useGeneratedCode from './utils/useGeneratedCode';
import useAddDomainModal from './utils/useAddDomainModal';
import useCompanyDomainsOptions from './utils/useCompanyDomainsOptions';
import useSupportedMappingFieldsOptions from './utils/useSupportedMappingFieldsOptions';
import SsoFormActions from './components/SsoFormActions';
import {
  AttributeMappingFieldNames,
  SsoIntegrationFieldNames,
} from './utils/constants';

export default function SsoIntegrationForm({ isEdit }: { isEdit?: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const { companyDomainsLoadOptions } = useCompanyDomainsOptions();
  const { supportedMappingFields } = useSupportedMappingFieldsOptions();

  const { data, onSubmitForm, loading, onCancel, firstLoading, graphQLErrors } =
    useSsoFormActions({ isEdit, supportedMappingFields });

  const {
    regenerateCode,
    isRegenerating,
    initialCode,
    codeFirstLoading,
    codeError,
  } = useGeneratedCode({
    formRef,
    skip: !!isEdit,
    t,
  });

  useEffect(() => {
    if (codeError) {
      showGraphQLErrors(codeError, t);
    }
  }, [codeError, t]);

  const isFormDataLoading = firstLoading || codeFirstLoading;

  const { handlePresetChange, applyPresetDefaults } = usePresetDefaults({
    formRef,
  });

  const presetDefaultsAppliedRef = useRef(false);

  useEffect(() => {
    if (
      isEdit &&
      !isFormDataLoading &&
      data?.providerPreset &&
      !presetDefaultsAppliedRef.current
    ) {
      presetDefaultsAppliedRef.current = true;
      applyPresetDefaults(data.providerPreset);
    }
  }, [isEdit, isFormDataLoading, data?.providerPreset, applyPresetDefaults]);

  const handleReadXml = useCallback((content: string) => {
    if (formRef.current?.setValue) {
      formRef.current.setValue(SsoIntegrationFieldNames.IdpMetadata, content, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, []);

  const {
    isDomainModalOpen,
    domainCacheKey,
    openAddDomainModal,
    closeAddDomainModal,
    onSubmitAddDomain,
    domainCreateMutationResult,
  } = useAddDomainModal({ formRef });

  const companyDomainIds = useMemo(
    () => data?.companyDomains?.map((d) => d.id) || [],
    [data],
  );

  const formFields = useMemo(
    () =>
      getSsoIntegrationFormFields({
        t,
        isEdit,
        companyDomainIds,
        domainCacheKey,
        getCompanyDomainsLoadOptions: companyDomainsLoadOptions,
        supportedMappingFields,
        onReadXml: handleReadXml,
        onPresetChange: handlePresetChange,
        openAddDomainModal,
        onRegenerateCode: regenerateCode,
        isRegeneratingCode: isRegenerating,
      }),
    [
      t,
      isEdit,
      companyDomainIds,
      domainCacheKey,
      companyDomainsLoadOptions,
      supportedMappingFields,
      handleReadXml,
      handlePresetChange,
      openAddDomainModal,
      regenerateCode,
      isRegenerating,
    ],
  );

  const defaultValues = useMemo(() => {
    const values = getDefaultValuesFromQueryData(
      data,
      t,
      supportedMappingFields,
    );
    if (!isEdit && initialCode) {
      return { ...values, [SsoIntegrationFieldNames.Code]: initialCode };
    }
    return values;
  }, [data, t, supportedMappingFields, isEdit, initialCode]);

  const breadCrumbsData = useMemo(
    () => getBreadCrumbsDataByKey({ pathname, t, entity: data }),
    [pathname, t, data],
  );

  const title = isEdit ? t('edit-sso-connection') : t('add-sso-connection');

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {isFormDataLoading ? (
        <CircleLoader />
      ) : (
        <DashboardContentMaxWidth>
          <PageHeader title={title} />
          <PageBody>
            <WhiteBox>
              <Form
                actionsComponent={SsoFormActions}
                actionsComponentProps={{ onCancel, isEdit }}
                defaultValues={defaultValues}
                fields={formFields}
                formRef={formRef}
                graphQLErrors={graphQLErrors}
                loading={loading}
                onSubmit={onSubmitForm}
                sourceAppendValues={{
                  [SsoIntegrationFieldNames.AttributeMapping]: {
                    [AttributeMappingFieldNames.Attribute]: '',
                    [AttributeMappingFieldNames.MapsTo]: null,
                  },
                }}
              />
            </WhiteBox>
          </PageBody>
        </DashboardContentMaxWidth>
      )}
      <AddDomainModalForm
        graphQLErrors={domainCreateMutationResult.error?.graphQLErrors}
        isOpen={isDomainModalOpen}
        loading={domainCreateMutationResult.loading}
        onClose={closeAddDomainModal}
        onSubmit={onSubmitAddDomain}
      />
    </>
  );
}
